import {DocumentDuplicateIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {Badge, Button, Link} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import KYCImage from '@/screens/KYC/Details/KYCImage';
import {fileService, kycBucketKey} from '@/services/fileService';
import {mapCountryCodeToFlags} from '@/utils/countries';
import * as dateUtils from '@/utils/date';
import {logRejection} from '@/utils/logRejection';
import {copyToClipboard} from '@/utils/user';

import type {MerchantUserDetailsProps} from './types';

// Contact values share one cell width, so anything longer is clipped with an
// ellipsis; a missing value shows the same placeholder the other rows use.
const truncateContactValue = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  return value.length > 15 ? `${value.slice(0, 15)}...` : value;
};

const MerchantUserProfile: React.FC<MerchantUserDetailsProps> = ({user}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [selfieBase64, setSelfieBase64] = useState<string | undefined>(
    undefined,
  );
  const country = user?.countryCode?.toUpperCase() || '';
  const flag = mapCountryCodeToFlags(countryList)[country];

  const selfie = user?.meta?.kyc?.data?.SELFIE || null;
  const phone = user?.meta?.kyc?.data?.PHONE || null;
  const email = user?.email || null;

  if (selfie && kycBucketKey) {
    // NOTE: this fetch runs in the render body rather than in an effect, so it
    // refires on every render until the resolved string settles (and again
    // after any parent re-render). That is pre-existing behaviour and is left
    // exactly as it is here; only the missing rejection handler is added, so a
    // failed S3 read is logged instead of becoming an unhandled rejection.
    fileService
      .s3ToBase64(selfie, kycBucketKey, user.id)
      .then((base64) => {
        setSelfieBase64(base64);
      })
      .catch(logRejection('Failed to load merchant user selfie'));
  }

  return (
    <>
      <div className="rounded-xl shadow-md border dark:border-dark-borderMid p-4 space-y-6 mt-6">
        <div className="border-b border-gray-200 dark:border-dark-borderMid">
          <div className="flex justify-center sm:justify-start flex-wrap items-center gap-3">
            <Link
              to={`/merchants/businesses/${user.businessId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="ghost" className="hover:underline">
                View Business Details
              </Button>
            </Link>

            <Link
              to={`/merchants/transactions/business/${user.businessId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="ghost" className="hover:underline">
                View Transactions
              </Button>
            </Link>

            <Link
              to={`/merchants/kyc/${user.id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-cy="merchant-user-profile-kyc-link"
            >
              <Button variant="ghost" className="hover:underline">
                View KYC Details
              </Button>
            </Link>

            <Link
              to={`/merchants/users/${user.id}/statement`}
              target="_blank"
              rel="noopener noreferrer"
              data-cy="merchant-user-profile-statement-link"
            >
              <Button variant="ghost" className="hover:underline">
                View Account Statement
              </Button>
            </Link>
          </div>
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-6">
          <div className="w-full xl:w-1/3 align-middle self-center">
            {/* Selfie */}
            <div className="mb-5 rounded-md border dark:border-dark-borderMid place-items-center self-center">
              <KYCImage src={selfieBase64} alt="User selfie" />
            </div>
          </div>
          {/* Basic KYC Info */}
          <div className="space-y-2 self-center w-full">
            {/* --- User Info --- */}
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Name
              </p>
              <p
                className="flex-[0.6] text-sm font-medium text-gray-700  dark:text-gray-100 w-1/2 truncate"
                data-cy="merchant-user-profile-name"
              >
                {user.name?.fullName}
              </p>
            </div>

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
                Email
              </p>
              <div className="flex-[0.6] text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                <div className="flex items-center gap-2 truncate">
                  <span
                    className="truncate"
                    data-cy="merchant-user-profile-email"
                  >
                    {truncateContactValue(email)}
                  </span>
                  {email && (
                    <span
                      className="truncate"
                      title="click to copy email"
                      onClick={() => copyToClipboard(email)}
                      data-cy="merchant-user-profile-copy-email"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* --- User Phone --- */}
            <div
              className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900"
              data-cy="merchant-user-profile-phone-row"
            >
              <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
                Phone
              </p>
              <div className="flex-[0.6] text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                <div className="flex items-center gap-2 truncate">
                  <span
                    className="truncate"
                    data-cy="merchant-user-profile-phone"
                  >
                    {truncateContactValue(phone)}
                  </span>
                  {phone && (
                    <span
                      className="truncate"
                      title="click to copy phone"
                      onClick={() => copyToClipboard(phone)}
                      data-cy="merchant-user-profile-copy-phone"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                User ID
              </p>
              <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
                <div className="flex-1 flex items-center gap-2 truncate">
                  <span
                    className="truncate"
                    data-cy="merchant-user-profile-user-id"
                  >
                    {user.id}
                  </span>
                  <span
                    title="click to copy userID"
                    onClick={() => copyToClipboard(user.id)}
                    data-cy="merchant-user-profile-copy-user-id"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
                  </span>
                </div>
              </div>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Country
              </p>
              <p
                className="flex-[0.6] text-sm font-medium text-gray-700  dark:text-gray-100 w-1/2 truncate"
                data-cy="merchant-user-profile-country"
              >
                {`${country || ''} ${flag ?? '-'}`}
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Date Opened
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                {dateUtils.DDMonYYYY(user?.createdAt?.toString() || '')}
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Role
              </p>
              <p
                className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 "
                data-cy="merchant-user-profile-role"
              >
                {user.role}
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Verified
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="merchant-user-profile-verified"
                  label={user?.meta?.flags?.isVerified ? 'Yes' : 'No'}
                  colorScheme={
                    user?.meta?.flags?.isVerified ? 'successful' : 'failed'
                  }
                  className="px-4"
                />
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Security Flagged
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="merchant-user-profile-security-flagged"
                  label={user?.meta?.flags?.isSecurityFlagged ? 'Yes' : 'No'}
                  colorScheme={
                    user?.meta?.flags?.isSecurityFlagged
                      ? 'failed'
                      : 'successful'
                  }
                  className="px-4"
                />
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Deactivated
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="merchant-user-profile-deactivated"
                  label={user?.meta?.flags?.isDeactivated ? 'Yes' : 'No'}
                  colorScheme={
                    user?.meta?.flags?.isDeactivated ? 'failed' : 'successful'
                  }
                  className="px-4"
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MerchantUserProfile;
